"use client";

import { useEffect, useState } from "react";
import {
  Flag,
  Check,
  X,
  Eye,
  RefreshCw,
  MessageSquare,
  AlertTriangle,
  Clock,
  Mail,
  type LucideIcon,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Stat,
  TabBar,
  List,
  ListItem,
  Spinner,
  Empty,
  Dialog,
  ConfirmDialog,
  useToast,
  Button,
} from "@/components/ui";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";

interface ModerationItem {
  id: string;
  title: string;
  proposeeName: string;
  status: string;
  createdAt: string;
  flagged?: boolean;
  rejectionReason?: string | null;
  appealStatus?: string;
  user: { id: string; name: string | null; email: string };
}

type TabType = "flagged" | "appeals";

const STATUS_BADGE_VARIANT: Record<string, "success" | "error" | "warning" | "neutral" | "info"> = {
  PUBLISHED: "success",
  ACCEPTED: "success",
  DECLINED: "error",
  UNDER_REVIEW: "warning",
  PENDING: "warning",
  DRAFT: "neutral",
};

export default function ModerationPage() {
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [appeals, setAppeals] = useState<ModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("flagged");

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const [confirmAppealAction, setConfirmAppealAction] = useState<{
    id: string;
    action: "approve" | "reject";
  } | null>(null);

  const { showToast } = useToast();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get<{ success: boolean; items: ModerationItem[] }>(
        "/admin/moderation/flagged",
      );
      if (res.success) setItems(res.items);
    } catch (e: any) {
      showToast({ title: "Failed to load moderation queue", description: e?.message, variant: "error" });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppeals = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get<{ success: boolean; appeals: ModerationItem[] }>(
        "/admin/moderation/appeals",
      );
      if (res.success) setAppeals(res.appeals);
    } catch (e: any) {
      showToast({ title: "Failed to load appeals", description: e?.message, variant: "error" });
      setAppeals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "flagged") fetchItems();
    else fetchAppeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const refresh = () => (activeTab === "flagged" ? fetchItems() : fetchAppeals());

  const handleApprove = async (proposalId: string) => {
    setProcessing(proposalId);
    try {
      await adminApi.post(`/admin/moderation/${proposalId}/approve`, {});
      showToast({ title: "Approved", variant: "success" });
      refresh();
    } catch (e: any) {
      showToast({ title: "Failed to approve", description: e?.message, variant: "error" });
    } finally {
      setProcessing(null);
    }
  };

  const openRejectModal = (proposalId: string) => {
    setRejectingId(proposalId);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    setProcessing(rejectingId);
    try {
      await adminApi.post(`/admin/moderation/${rejectingId}/reject`, { reason: rejectReason });
      showToast({ title: "Rejected", variant: "info" });
      setShowRejectModal(false);
      setRejectingId(null);
      refresh();
    } catch (e: any) {
      showToast({ title: "Failed to reject", description: e?.message, variant: "error" });
    } finally {
      setProcessing(null);
    }
  };

  const handleAppealAction = async (proposalId: string, action: "approve" | "reject") => {
    setProcessing(proposalId);
    try {
      await adminApi.post(`/admin/moderation/${proposalId}/appeal-action`, {
        action,
        reason: action === "reject" ? rejectReason : undefined,
      });
      showToast({ title: action === "approve" ? "Appeal approved" : "Appeal rejected", variant: action === "approve" ? "success" : "info" });
      fetchAppeals();
    } catch (e: any) {
      showToast({ title: "Failed to process appeal", description: e?.message, variant: "error" });
    } finally {
      setProcessing(null);
    }
  };

  const confirmAndAct = () => {
    if (!confirmAppealAction) return;
    const { id, action } = confirmAppealAction;
    setConfirmAppealAction(null);
    void handleAppealAction(id, action);
  };

  const pendingCount = items.filter(
    (i) => i.status !== "PUBLISHED" && i.status !== "ACCEPTED",
  ).length;
  const underReviewCount = items.filter((i) => i.status === "UNDER_REVIEW").length;

  const displayItems = activeTab === "flagged" ? items : appeals;

  const tabOptions: Array<{ value: TabType; label: React.ReactNode }> = [
    {
      value: "flagged",
      label: (
        <span className="inline-flex items-center gap-2">
          <Flag className="h-3.5 w-3.5" />
          Flagged
          <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/10 px-1 text-[10px] font-semibold tabular-nums text-white/80">
            {items.length}
          </span>
        </span>
      ),
    },
    {
      value: "appeals",
      label: (
        <span className="inline-flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" />
          Appeals
          <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/10 px-1 text-[10px] font-semibold tabular-nums text-white/80">
            {appeals.length}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Trust & safety"
        title="Moderation"
        subtitle="Review flagged content and resolve appeals from rejected proposals."
        actions={
          <Button variant="secondary" size="md" onClick={refresh} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Refresh
          </Button>
        }
      />

      {/* Tab bar — Apple TabBar with sliding pink underline */}
      <div className="mt-4">
        <TabBar<TabType> value={activeTab} options={tabOptions} onChange={setActiveTab} />
      </div>

      {/* Stats */}
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="Pending Review"
          value={pendingCount.toLocaleString()}
          tint="orange"
          icon={<Flag className="h-4 w-4" />}
        />
        <Stat
          label="Under Review"
          value={underReviewCount.toLocaleString()}
          tint="blue"
          icon={<Eye className="h-4 w-4" />}
        />
        <Stat
          label={activeTab === "flagged" ? "Total Flagged" : "Pending Appeals"}
          value={(activeTab === "flagged" ? items.length : appeals.length).toLocaleString()}
          tint="rose"
          icon={<AlertTriangle className="h-4 w-4" />}
        />
      </div>

      {/* Queue */}
      <Card className="!p-0 mt-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="!h-6 !w-6" />
          </div>
        ) : displayItems.length === 0 ? (
          <Empty
            icon={activeTab === "flagged" ? <Flag className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
            title={activeTab === "flagged" ? "No flagged content" : "No pending appeals"}
            message={activeTab === "flagged" ? "All clear — no proposals need review." : "Nothing to action right now."}
          />
        ) : (
          <List className="!rounded-none">
            {displayItems.map((item, i) => (
              <ModerationRow
                key={item.id}
                item={item}
                isAppeals={activeTab === "appeals"}
                processing={processing === item.id}
                noSeparator={i === 0}
                onApprove={() => handleApprove(item.id)}
                onReject={() => openRejectModal(item.id)}
                onAppealApprove={() => setConfirmAppealAction({ id: item.id, action: "approve" })}
                onAppealReject={() => openRejectModal(item.id)}
              />
            ))}
          </List>
        )}
      </Card>

      {/* Reject modal */}
      <Dialog
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectingId(null);
          setRejectReason("");
        }}
        title={activeTab === "appeals" ? "Reject appeal" : "Reject proposal"}
        message="Optionally leave a reason — it will be shown to the proposer."
        width={460}
      >
        <div className="mt-2 space-y-3">
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection (optional)"
            rows={3}
            className="w-full rounded-xl border border-line bg-abyss/60 px-3 py-2 text-[13px] text-cream placeholder:text-dim outline-none transition focus:border-rose/40"
          />
        </div>
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectingId(null);
              setRejectReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleReject}
            disabled={!!processing}
          >
            {processing ? <Spinner className="!h-3.5 !w-3.5" /> : null}
            {activeTab === "appeals" ? "Reject appeal" : "Reject proposal"}
          </Button>
        </div>
      </Dialog>

      {/* Confirm appeal approve */}
      <ConfirmDialog
        open={!!confirmAppealAction && confirmAppealAction.action === "approve"}
        onClose={() => setConfirmAppealAction(null)}
        onConfirm={confirmAndAct}
        title="Approve appeal?"
        description="This will restore the proposal to its previous state and notify the proposer."
        confirmLabel="Approve appeal"
        variant="primary"
        loading={!!processing}
      />
    </div>
  );
}

/* ─── Row renderer ─────────────────────────────────────────────────────── */

function ModerationRow({
  item,
  isAppeals,
  processing,
  noSeparator,
  onApprove,
  onReject,
  onAppealApprove,
  onAppealReject,
}: {
  item: ModerationItem;
  isAppeals: boolean;
  processing: boolean;
  noSeparator: boolean;
  onApprove: () => void;
  onReject: () => void;
  onAppealApprove: () => void;
  onAppealReject: () => void;
}) {
  const statusVariant = STATUS_BADGE_VARIANT[item.status] ?? "neutral";
  const creatorInitial = ((item.user.name || item.user.email) || "?").charAt(0).toUpperCase();

  const LeadingIcon: LucideIcon = isAppeals ? MessageSquare : Flag;

  return (
    <ListItem
      noSeparator={noSeparator}
      leading={<LeadingIcon className="h-4 w-4" />}
      leadingTint={isAppeals ? "purple" : "orange"}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{item.title}</span>
          <StatusBadge variant={statusVariant}>{item.status}</StatusBadge>
          {item.appealStatus && <StatusBadge variant="info">{item.appealStatus}</StatusBadge>}
        </span>
      }
      subtitle={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>For {item.proposeeName}</span>
          <span className="inline-flex items-center gap-1">
            <span className="font-semibold">{creatorInitial}</span>
            {item.user.name || item.user.email}
          </span>
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {item.user.email}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(item.createdAt).toLocaleString()}
          </span>
          {isAppeals && item.rejectionReason && (
            <span className="text-blush/90">“{item.rejectionReason}”</span>
          )}
        </span>
      }
      trailing={
        isAppeals ? (
          <div className="flex items-center gap-2">
            <Button
              variant="success"
              size="sm"
              disabled={processing}
              onClick={onAppealApprove}
              title="Approve appeal"
            >
              {processing ? <Spinner className="!h-3.5 !w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              Approve
            </Button>
            <Button
              variant="success"
              size="sm"
              disabled={processing}
              onClick={onAppealReject}
              title="Reject appeal"
            >
              {processing ? <Spinner className="!h-3.5 !w-3.5" /> : <X className="h-3.5 w-3.5" />}
              Reject
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="success"
              size="sm"
              disabled={processing}
              onClick={onApprove}
              title="Approve"
            >
              {processing ? <Spinner className="!h-3.5 !w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              Approve
            </Button>
            <Button
              variant="success"
              size="sm"
              disabled={processing}
              onClick={onReject}
              title="Reject"
            >
              {processing ? <Spinner className="!h-3.5 !w-3.5" /> : <X className="h-3.5 w-3.5" />}
              Reject
            </Button>
          </div>
        )
      }
    />
  );
}

/* ─── Apple-style status badge ─────────────────────────────────────────── */

function StatusBadge({
  variant,
  children,
}: {
  variant: "success" | "error" | "warning" | "neutral" | "info";
  children: React.ReactNode;
}) {
  const tintMap: Record<string, string> = {
    success: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
    error:   "bg-red-500/15 text-red-300 ring-red-400/30",
    warning: "bg-amber-500/15 text-amber-200 ring-amber-400/30",
    info:    "bg-blue-500/15 text-blue-300 ring-blue-400/30",
    neutral: "bg-white/8 text-white/70 ring-white/15",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset",
        tintMap[variant],
      )}
    >
      {children}
    </span>
  );
}
